// Signing in as a person: an emailed link, Google or Microsoft, then the
// second factor. Shared by the staff portal (`App.js`, when its key is
// missing or refused) and the account console (`Console.js`).
//
// **The shared key still works exactly as it did.** WordPress keeps putting
// `?token=` on the iframe and the portal keeps sending it; this is the other
// way in, for somebody opening the portal on its own. What it produces is a
// session token, kept in `localStorage` so a sign-in lasts across tabs, and
// sent in the same `x-portal-token` header the shared key always used — so
// every request the portal makes carries it without being touched. The
// middleware tells the two apart by the `s32s.` prefix.
//
// **The session never goes on a URL.** Document links are built on a read
// token the middleware hands out (`/document-token`), and the session is not
// their fallback. See docs/decisions/staff-sign-in.md.
//
// The middleware decides every rule here — who must enrol, what a role may do,
// which providers are on. This file draws what `/accounts/me` and
// `/accounts/providers` say.

import { useState, useEffect, useRef, useCallback } from "react";
import QRCode from "qrcode";
import { T, MIDDLEWARE_URL, LOGO_SRC } from "./shared";

const SESSION_KEY = "provey.staffSession";
const SESSION_PREFIX = "s32s.";

export const isSessionToken = (token) => String(token || "").startsWith(SESSION_PREFIX);

export const readStoredSession = () => {
  try { return localStorage.getItem(SESSION_KEY) || ""; } catch { return ""; }
};
export const storeSession = (token) => {
  try { localStorage.setItem(SESSION_KEY, token); } catch { /* storage blocked — this tab still has it */ }
};
export const clearSession = () => {
  try { localStorage.removeItem(SESSION_KEY); } catch { /* storage blocked */ }
};

// ── The sign-in hand-off, read once at module load ─────────────────────────
//
// `?signin=` is a one-use code (an emailed link, or the hand-off back from
// Google or Microsoft); `?signin_error=` is the reason a provider sign-in
// failed. Both are taken out of the address bar at once, and read here rather
// than in an effect because StrictMode runs effects twice in development — the
// second redemption would be told the link had already been used.
const SIGNIN = (() => {
  try {
    const url = new URL(window.location.href);
    const token = url.searchParams.get("signin") || "";
    const error = url.searchParams.get("signin_error") || "";
    if (token || error) {
      url.searchParams.delete("signin");
      url.searchParams.delete("signin_error");
      window.history.replaceState({}, "", url.toString());
    }
    return { token, error };
  } catch { return { token: "", error: "" }; }
})();

export const signInPending = () => Boolean(SIGNIN.token || SIGNIN.error);

const SIGNIN_ERRORS = {
  cancelled: "Sign-in was cancelled.",
  state: "That sign-in took too long or was started in another browser. Try again.",
  provider: "We could not complete that sign-in. Try again, or use an email link.",
  unverified: "That account's email address is not verified with its provider.",
  no_account: "There is no account for that address. Ask your firm's administrator to add you.",
  mismatch: "A different account is already linked to that address. Use the one you signed in with before, or an email link.",
  unavailable: "That sign-in method is not available.",
};

export const accountsFetch = (path, options) => staffFetch(`/accounts${path}`, options);

// The same request to any middleware path, on the signed-in session. For a
// staff screen in a file that cannot import App.js's own fetch.
export async function staffFetch(path, { method = "GET", body, token = readStoredSession() } = {}) {
  const res = await fetch(`${MIDDLEWARE_URL}${path}`, {
    method,
    headers: {
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { "x-portal-token": token } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok || !data?.ok) {
    const err = new Error(data?.error || `The request failed (${res.status})`);
    err.status = res.status;
    err.code = data?.code;
    err.body = data;
    throw err;
  }
  return data;
}

let redeeming = null;
const redeemOnce = (token) => {
  if (!redeeming) redeeming = accountsFetch("/sign-in/redeem", { method: "POST", body: { token }, token: "" });
  return redeeming;
};

export async function signOut() {
  const token = readStoredSession();
  clearSession();
  if (token) await accountsFetch("/sign-out", { method: "POST", token }).catch(() => {});
}

// ── Pieces ─────────────────────────────────────────────────────────────────

export const Btn = ({ children, variant = "primary", style, ...props }) => {
  const look = {
    primary: { background: T.brown, color: T.warmGrey, border: `1px solid ${T.brown}` },
    secondary: { background: T.white, color: T.text, border: `1px solid ${T.borderMid}` },
    danger: { background: T.white, color: T.danger, border: `1px solid ${T.dangerStroke}` },
    quiet: { background: "none", color: T.textSoft, border: "1px solid transparent" },
  }[variant];
  return (
    <button type="button" {...props} style={{
      ...look, borderRadius: 999, padding: "8px 16px", fontSize: 13, fontWeight: 500,
      fontFamily: "Inter, sans-serif", cursor: props.disabled ? "default" : "pointer",
      opacity: props.disabled ? 0.55 : 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
      ...style,
    }}>{children}</button>
  );
};

export const inputStyle = {
  width: "100%", boxSizing: "border-box", padding: "9px 12px", fontSize: 14, fontFamily: "Inter, sans-serif",
  border: `1px solid ${T.borderMid}`, borderRadius: 8, background: T.white, color: T.text,
};

export const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} style={{ display: "block", fontSize: 12, fontWeight: 500, color: T.textSoft, margin: "0 0 6px" }}>{children}</label>
);

export const Note = ({ tone = "ink", children }) => {
  const colours = {
    ink: { background: T.bg, color: T.text, border: T.border },
    red: { background: T.dangerTint, color: T.danger, border: T.dangerStroke },
    warn: { background: T.warnTint, color: T.warn, border: T.warnStroke },
  }[tone];
  return (
    <div role={tone === "red" ? "alert" : "status"} style={{ background: colours.background, color: colours.color,
      border: `1px solid ${colours.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 13, lineHeight: 1.5 }}>{children}</div>
  );
};

const Card = ({ children, width = 420 }) => (
  <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: 32, maxWidth: width, width: "100%", boxSizing: "border-box" }}>
    {children}
  </div>
);

const Heading = ({ children }) => (
  <h1 style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 22, color: T.text, margin: "0 0 8px", letterSpacing: "-0.02em" }}>{children}</h1>
);

const Lead = ({ children }) => (
  <p style={{ fontSize: 13, color: T.textSoft, margin: "0 0 20px", lineHeight: 1.6 }}>{children}</p>
);

const ProviderIcon = ({ provider }) => (provider === "google" ? (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.6 12.2c0-.8-.1-1.5-.2-2.2H12v4.3h5.9a5 5 0 0 1-2.2 3.3v2.7h3.6c2.1-1.9 3.3-4.8 3.3-8.1z" />
    <path fill="#34A853" d="M12 23c3 0 5.5-1 7.3-2.7l-3.6-2.7c-1 .7-2.2 1.1-3.7 1.1-2.9 0-5.3-1.9-6.2-4.5H2.1v2.8A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.8 14.2a6.6 6.6 0 0 1 0-4.3V7.1H2.1a11 11 0 0 0 0 9.9l3.7-2.8z" />
    <path fill="#EA4335" d="M12 5.4c1.6 0 3.1.6 4.2 1.7l3.2-3.2A11 11 0 0 0 2.1 7.1l3.7 2.8C6.7 7.3 9.1 5.4 12 5.4z" />
  </svg>
) : (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#F25022" d="M2 2h9.5v9.5H2z" /><path fill="#7FBA00" d="M12.5 2H22v9.5h-9.5z" />
    <path fill="#00A4EF" d="M2 12.5h9.5V22H2z" /><path fill="#FFB900" d="M12.5 12.5H22V22h-9.5z" />
  </svg>
));

// ── Setting up an authenticator ─────────────────────────────────────────────

export function RecoveryCodes({ codes, onDone }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try {
      navigator.clipboard.writeText(codes.join("\n")).then(() => setCopied(true)).catch(() => {});
    } catch { /* no clipboard — the codes are on screen */ }
  };
  return (
    <div>
      <Lead>
        Keep these recovery codes somewhere safe. Each one signs you in once if you lose your phone.
        They will not be shown again.
      </Lead>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, background: T.bg, borderRadius: 8, padding: 14,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 14, marginBottom: 16 }}>
        {codes.map(code => <span key={code}>{code}</span>)}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Btn variant="secondary" onClick={copy}>{copied ? "Copied" : "Copy codes"}</Btn>
        {onDone && <Btn onClick={onDone}>I have saved them</Btn>}
      </div>
    </div>
  );
}

export function TwoFactorSetup({ token, onDone }) {
  const [setup, setSetup] = useState(null);
  const [qr, setQr] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    accountsFetch("/two-factor/setup", { method: "POST", token })
      .then((data) => {
        setSetup(data);
        return QRCode.toDataURL(data.uri, { margin: 1, width: 200 }).then(setQr).catch(() => {});
      })
      .catch(e => setError(e.message));
  }, [token]);

  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = await accountsFetch("/two-factor/confirm", { method: "POST", token, body: { code } });
      setCodes(data.recoveryCodes);
    } catch (err) {
      setError(err.code === "signed_out" ? "Too many wrong codes. Sign in again." : err.message);
      if (err.code === "signed_out") clearSession();
    } finally {
      setBusy(false);
    }
  };

  if (codes) return <RecoveryCodes codes={codes} onDone={onDone} />;
  return (
    <form onSubmit={confirm}>
      <Lead>
        Scan this with an authenticator app — Google Authenticator, Microsoft Authenticator, 1Password — then
        enter the six-digit code it shows.
      </Lead>
      {setup && (
        <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
          {qr && <img src={qr} alt="QR code for your authenticator app" width={160} height={160} style={{ border: `1px solid ${T.border}`, borderRadius: 8 }} />}
          <div style={{ fontSize: 12, color: T.textSoft, minWidth: 160, flex: 1 }}>
            Can't scan it? Enter this key:
            <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13, color: T.text, wordBreak: "break-all", marginTop: 6 }}>
              {setup.secret.replace(/(.{4})/g, "$1 ").trim()}
            </div>
          </div>
        </div>
      )}
      <Label htmlFor="s32-totp-setup">Code from the app</Label>
      <input id="s32-totp-setup" value={code} onChange={e => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code"
        maxLength={7} style={{ ...inputStyle, letterSpacing: "0.3em", fontSize: 18, marginBottom: 12 }} />
      {error && <div style={{ marginBottom: 12 }}><Note tone="red">{error}</Note></div>}
      <Btn type="submit" disabled={busy || !setup || code.replace(/\s/g, "").length !== 6}>
        {busy ? "Checking…" : "Turn on two-step sign-in"}
      </Btn>
    </form>
  );
}

function SecondFactorVerify({ token, onDone, onRestart }) {
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = await accountsFetch("/two-factor/verify", {
        method: "POST", token, body: useRecovery ? { recoveryCode: code } : { code },
      });
      onDone(data);
    } catch (err) {
      if (err.code === "signed_out") {
        clearSession();
        onRestart("Too many wrong codes. Sign in again.");
        return;
      }
      setError(err.body?.attemptsLeft ? `${err.message} ${err.body.attemptsLeft} attempts left.` : err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={verify}>
      <Lead>{useRecovery ? "Enter one of your recovery codes." : "Enter the six-digit code from your authenticator app."}</Lead>
      <Label htmlFor="s32-totp">{useRecovery ? "Recovery code" : "Code"}</Label>
      <input id="s32-totp" value={code} onChange={e => setCode(e.target.value)} autoFocus
        inputMode={useRecovery ? "text" : "numeric"} autoComplete="one-time-code" maxLength={useRecovery ? 12 : 7}
        style={{ ...inputStyle, letterSpacing: useRecovery ? "0.1em" : "0.3em", fontSize: 18, marginBottom: 12 }} />
      {error && <div style={{ marginBottom: 12 }}><Note tone="red">{error}</Note></div>}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <Btn type="submit" disabled={busy || !code.trim()}>{busy ? "Checking…" : "Continue"}</Btn>
        <Btn variant="quiet" onClick={() => { setUseRecovery(!useRecovery); setCode(""); setError(""); }}>
          {useRecovery ? "Use the app instead" : "Lost your phone? Use a recovery code"}
        </Btn>
      </div>
    </form>
  );
}

// ── The sign-in screen ─────────────────────────────────────────────────────
//
// `onSignedIn(account)` is called once nothing more is owed. A stored session
// that is still good goes straight through; one waiting on its second factor
// resumes there; anything else starts again.
//
// `setupStep({ account, token, onDone })`, where given, is shown once the
// sign-in (and the second factor) is done and before `onSignedIn` — the
// firm's own set-up questions, which decide for themselves whether anything
// is owed and call `onDone` straight away when not. It is a render prop so
// this file imports nothing of the screens that ask them.
//
// `refusedToken` is the credential the page that sent us here just had
// refused. A finished stored session that *is* that token is not passed
// straight back — that would loop — and is told so instead.
export function SignInScreen({ returnPath = "/", intro, onSignedIn, refusedToken = "", setupStep }) {
  const [phase, setPhase] = useState(SIGNIN.token ? "redeeming" : "checking");
  const [providers, setProviders] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(SIGNIN_ERRORS[SIGNIN.error] || (SIGNIN.error ? SIGNIN_ERRORS.provider : ""));
  const [account, setAccount] = useState(null);
  const [token, setToken] = useState("");
  const done = useRef(false);

  // Stable, so a set-up step may depend on it.
  const signedInRef = useRef(onSignedIn);
  signedInRef.current = onSignedIn;
  const accountRef = useRef(null);
  const finishSetup = useCallback(() => signedInRef.current(accountRef.current), []);

  const finish = (acct) => {
    if (done.current) return;
    done.current = true;
    if (setupStep) {
      accountRef.current = acct;
      setAccount(acct);
      setToken(t => t || readStoredSession());
      setPhase("setup");
      return;
    }
    onSignedIn(acct);
  };

  const arrive = (sessionToken, acct) => {
    storeSession(sessionToken);
    setToken(sessionToken);
    setAccount(acct);
    if (acct.secondFactor.done) finish(acct);
    else setPhase("second");
  };

  useEffect(() => {
    accountsFetch("/providers", { token: "" }).then(setProviders).catch(() => setProviders({ email: true }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (SIGNIN.token) {
      redeemOnce(SIGNIN.token)
        .then(data => { if (!cancelled) arrive(data.sessionToken, data.account); })
        .catch((e) => { if (!cancelled) { setMessage(e.message); setPhase("start"); } });
      return () => { cancelled = true; };
    }
    const stored = readStoredSession();
    if (!stored) { setPhase("start"); return undefined; }
    accountsFetch("/me", { token: stored })
      .then((data) => {
        if (cancelled) return;
        if (data.account.secondFactor.done && stored !== refusedToken) {
          finish(data.account);
        } else if (data.account.secondFactor.done) {
          // Signed in, and still refused by whatever sent us here: the session
          // is good but does not open this. Say so rather than loop.
          setAccount(data.account);
          setToken(stored);
          setPhase("signed_in");
        } else {
          arrive(stored, data.account);
        }
      })
      .catch(() => { if (!cancelled) { clearSession(); setPhase("start"); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const askForLink = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const data = await accountsFetch("/sign-in/email", { method: "POST", body: { email, returnPath, origin: window.location.origin }, token: "" });
      setMessage(data.message);
      setPhase("sent");
    } catch (err) {
      setMessage(err.message);
    } finally {
      setBusy(false);
    }
  };

  const withProvider = (provider) => {
    // The origin lets a pull request's preview get the sign-in back rather than
    // the live portal; the middleware honours only the portal's own channels.
    const params = new URLSearchParams({ return: returnPath, origin: window.location.origin });
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) params.set("hint", email.trim());
    window.location.assign(`${MIDDLEWARE_URL}/accounts/oauth/${provider}/start?${params.toString()}`);
  };

  const restart = (why = "") => {
    clearSession();
    setToken("");
    setAccount(null);
    setMessage(why);
    setPhase("start");
  };

  let body;
  if (phase === "setup" && account) {
    body = setupStep({ account, token: token || readStoredSession(), onDone: finishSetup });
  } else if (phase === "checking" || phase === "redeeming") {
    body = <Lead>{phase === "redeeming" ? "Signing you in…" : "Checking your sign-in…"}</Lead>;
  } else if (phase === "second" && account) {
    const enrol = account.secondFactor.step === "enrol";
    body = (
      <>
        <Heading>{enrol ? "Set up two-step sign-in" : "Two-step sign-in"}</Heading>
        {enrol && <Lead>{account.firm.name || "Your firm"} asks everybody to use an authenticator app as well as their email.</Lead>}
        {enrol
          ? <TwoFactorSetup token={token} onDone={() => finish({ ...account, secondFactor: { ...account.secondFactor, done: true, enabled: true } })} />
          : <SecondFactorVerify token={token} onDone={() => finish({ ...account, secondFactor: { ...account.secondFactor, done: true } })} onRestart={restart} />}
        <div style={{ marginTop: 16 }}><Btn variant="quiet" onClick={() => signOut().then(() => restart())}>Not you? Sign out</Btn></div>
      </>
    );
  } else if (phase === "signed_in" && account) {
    body = (
      <>
        <Heading>Signed in as {account.user.name || account.user.email}</Heading>
        <Lead>Your account is signed in, but it does not open this page.</Lead>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Btn onClick={() => finish(account)}>Continue</Btn>
          <Btn variant="secondary" onClick={() => signOut().then(() => restart())}>Sign out</Btn>
        </div>
      </>
    );
  } else if (phase === "sent") {
    body = (
      <>
        <Heading>Check your email</Heading>
        <Lead>{message}</Lead>
        <Btn variant="secondary" onClick={() => { setMessage(""); setPhase("start"); }}>Use a different address</Btn>
      </>
    );
  } else {
    body = (
      <>
        <Heading>Sign in</Heading>
        <Lead>{intro || "Sign in with your work email, or with Google or Microsoft."}</Lead>
        {message && <div style={{ marginBottom: 16 }}><Note tone="red">{message}</Note></div>}
        {(providers?.google || providers?.microsoft) && (
          <div style={{ display: "grid", gap: 8, marginBottom: 18 }}>
            {providers.google && (
              <Btn variant="secondary" onClick={() => withProvider("google")} style={{ width: "100%" }}>
                <ProviderIcon provider="google" /> Continue with Google
              </Btn>
            )}
            {providers.microsoft && (
              <Btn variant="secondary" onClick={() => withProvider("microsoft")} style={{ width: "100%" }}>
                <ProviderIcon provider="microsoft" /> Continue with Microsoft
              </Btn>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 10, color: T.textFaint, fontSize: 12, margin: "6px 0" }}>
              <span style={{ flex: 1, height: 1, background: T.border }} />or<span style={{ flex: 1, height: 1, background: T.border }} />
            </div>
          </div>
        )}
        <form onSubmit={askForLink}>
          <Label htmlFor="s32-signin-email">Work email</Label>
          <input id="s32-signin-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
            autoComplete="email" style={{ ...inputStyle, marginBottom: 12 }} />
          <Btn type="submit" disabled={busy || !email.trim()} style={{ width: "100%" }}>
            {busy ? "Sending…" : "Email me a sign-in link"}
          </Btn>
        </form>
      </>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 20px", fontFamily: "Inter, sans-serif" }}>
      <Card>
        <img src={LOGO_SRC} alt="Provey" style={{ height: 20, width: "auto", display: "block", marginBottom: 24 }} />
        {body}
      </Card>
    </div>
  );
}
